import type { LayoutSettings } from "../types";
import type { SlotsToClasses } from "@heroui/react";
import clsx from "clsx";
import { useMemo } from "react";
import type { ContainerProps } from "../types";
import type { CSSProperties } from "styled-components";

export interface VGroveFooterProps extends ContainerProps {
    settings: LayoutSettings;
    style?: CSSProperties;

    // 模糊效果控制
    isBlurred?: boolean;

    // classNames 支持不同部分的样式定制
    classNames?: SlotsToClasses<"footer" | "container" | "content" | "leftArea" | "middleArea" | "rightArea">;

    // 左侧区域：品牌/版权信息
    leftContent?: React.ReactNode | ((props: { className?: string }) => React.ReactNode);

    // 中间区域：导航链接/自定义内容
    middleContent?: React.ReactNode | ((props: { className?: string }) => React.ReactNode);

    // 右侧区域：链接/自定义内容
    rightContent?: React.ReactNode | ((props: { className?: string }) => React.ReactNode);
}

export const VGroveFooter: React.FC<VGroveFooterProps> = ({
    settings,
    className,
    classNames,
    style,
    children,
    leftContent,
    middleContent,
    rightContent,
    isBlurred = true,
}) => {
    const footerConfig = settings.footerConfig;
    const isSticky = footerConfig?.sticky ?? false;
    const height = footerConfig?.height || 'auto';

    // 使用 useMemo 缓存渲染内容，避免重复计算
    const leftContentMemo = useMemo(() => {
        if (!leftContent) return null;

        if (typeof leftContent === 'function') {
            return leftContent({ className: "flex items-center gap-2" });
        }

        return leftContent;
    }, [leftContent]);

    const middleContentMemo = useMemo(() => {
        if (!middleContent) return null;

        if (typeof middleContent === 'function') {
            return middleContent({ className: "flex items-center gap-2" });
        }

        return middleContent;
    }, [middleContent]);

    const rightContentMemo = useMemo(() => {
        if (!rightContent) return null;

        if (typeof rightContent === 'function') {
            return rightContent({ className: "flex items-center gap-2" });
        }

        return rightContent;
    }, [rightContent]);

    // 如果提供了 children，使用 children 而不是左右结构
    if (children) {
        return (
            <footer
                className={clsx(
                    "w-full",
                    "border-t-small",
                    "border-divider/20",
                    "transition-all",
                    "duration-300",
                    "ease-in-out",
                    {
                        'fixed bottom-0 z-40': isSticky,
                        'relative': !isSticky,
                        'bg-background/95 backdrop-blur-xl': isBlurred,
                        'bg-background': !isBlurred,
                    },
                    classNames?.footer,
                    className
                )}
                style={{
                    height: height !== 'auto' ? height : undefined,
                    minHeight: height !== 'auto' ? height : '60px',
                    ...style
                }}
            >
                <div className={clsx("max-w-7xl mx-auto px-6 py-4", classNames?.container)}>
                    {children}
                </div>
            </footer>
        );
    }

    return (
        <footer
            className={clsx(
                "w-full",
                "border-t-small",
                "border-divider/20",
                "transition-all",
                "duration-300",
                "ease-in-out",
                {
                    'fixed bottom-0 z-40': isSticky,
                    'relative': !isSticky,
                    'bg-background/95 backdrop-blur-xl': isBlurred,
                    'bg-background': !isBlurred,
                },
                classNames?.footer,
                className
            )}
            style={{
                height: height !== 'auto' ? height : undefined,
                minHeight: height !== 'auto' ? height : '60px',
                ...style
            }}
        >
            <div className={clsx("max-w-7xl px-6 py-4", classNames?.container)}>
                <div className={clsx("flex flex-col md:flex-row items-center md:justify-between gap-4", classNames?.content)}>
                    {/* 左侧区域 */}
                    {leftContentMemo && (
                        <div className={clsx("flex items-center order-1", classNames?.leftArea)}>
                            {leftContentMemo}
                        </div>
                    )}

                    {/* 中间区域 */}
                    {middleContentMemo && (
                        <div className={clsx("flex items-center order-3 md:order-2", classNames?.middleArea)}>
                            {middleContentMemo}
                        </div>
                    )}

                    {/* 右侧区域 */}
                    {rightContentMemo && (
                        <div className={clsx("flex items-center order-2 md:order-3", classNames?.rightArea)}>
                            {rightContentMemo}
                        </div>
                    )}
                </div>
            </div>
        </footer>
    );
}; 